import {
  CButton,
  CCol,
  CForm,
  CFormFeedback,
  CFormSelect,
  CLoadingButton,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilPeople } from '@coreui/icons'
import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import PlayerService from 'src/service/PlayerService'
import ToastComponent from 'src/components/common/TaostComponent'
import CommonService from 'src/service/CommonService'
import ImageDropzone, { SUPPORTED_IMAGE_FORMATS, MAX_IMAGE_SIZE } from './BulkImage/ImageDropzone'
import PlayerReviewTable from './BulkImage/PlayerReviewTable'
import SectionBox from './BulkImage/SectionBox'

const PlayerPositionBulkUpload = (props) => {
  const [position, setPosition] = useState([])
  const [allPlayers, setAllPlayers] = useState([])
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null)
  const [loader, setLoader] = useState(false)

  useEffect(() => {
    PlayerService.getTeamPositionValueBatBowlStyle().then((result) => {
      setPosition(result.position)
    })
    CommonService.clubPlayers().then((result) => {
      setAllPlayers(result.data)
    })
  }, [])

  const validationSchema = Yup.object().shape({
    position: Yup.string().required('Position is required'),
    image: Yup.mixed()
      .nullable(true)
      .required('Image is required')
      .test('fileSize', 'File size too large, max file size is 2 MB', (file) => {
        if (file) {
          return file.size <= MAX_IMAGE_SIZE
        }
        return true
      })
      .test(
        'type',
        'Invalid file format, only JPG, PNG or WEBP is allowed',
        (value) => !value || (value && SUPPORTED_IMAGE_FORMATS.includes(value?.type)),
      ),
  })

  const formik = useFormik({
    initialValues: {
      position: '',
      image: null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      var formData = new FormData()
      formData.append('position', data.position)
      formData.append('image', data.image)
      setLoader(true)
      PlayerService.saveBulkPlayerPositionImage(formData).then((res) => {
        if (res.status === 200) {
          resetAll()
          props.setRefreshKey((prev) => prev + 1)
          ToastComponent(res.message, 'success')
        } else {
          ToastComponent(res.message, 'error')
        }
        setLoader(false)
      })
    },
  })

  const resetAll = () => {
    formik.resetForm({
      values: {
        position: '',
        image: null,
      },
    })
    setImagePreviewUrl(null)
  }

  const setImageFile = (file) => {
    formik.setTouched({ ...formik.touched, image: true })
    formik.setFieldValue('image', file)
    const reader = new FileReader()
    reader.onload = () => setImagePreviewUrl(reader.result)
    reader.readAsDataURL(file)
  }

  const matchedPlayers = formik.values.position
    ? allPlayers.filter((item) => String(item.position) === String(formik.values.position))
    : []
  const reviewPlayers = matchedPlayers.map((item) => ({
    id: item.value,
    name: item.text,
    team_name: item.team_name,
    image: item.image,
    is_avatar: item.is_avatar,
  }))

  return (
    <CForm onSubmit={formik.handleSubmit}>
      <CRow className="g-4">
        <CCol md={6}>
          <SectionBox title="1. Select Position">
            <CFormSelect
              aria-label="Select Position"
              name="position"
              className={
                'form-control' +
                (formik.errors.position && formik.touched.position ? ' is-invalid' : '')
              }
              value={formik.values.position}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              id="position"
            >
              <option value="">Select Position</option>
              {position &&
                position.map((item, key) => (
                  <option value={item?.id} key={key}>
                    {item?.name}
                  </option>
                ))}
            </CFormSelect>
            {formik.errors.position && formik.touched.position && (
              <CFormFeedback invalid>{formik.errors.position}</CFormFeedback>
            )}
            <div className="text-medium-emphasis small d-flex align-items-center mt-2">
              <CIcon icon={cilPeople} className="me-1" />
              {matchedPlayers.length} players matched
            </div>
          </SectionBox>
        </CCol>

        <CCol md={6}>
          <SectionBox title="2. Upload Image">
            <ImageDropzone
              onChange={setImageFile}
              error={formik.touched.image ? formik.errors.image : null}
              previewUrl={imagePreviewUrl}
            />
          </SectionBox>
        </CCol>
      </CRow>

      <PlayerReviewTable
        players={reviewPlayers}
        newImagePreviewUrl={imagePreviewUrl}
        emptyMessage="No players matched yet"
      />

      <div className="d-flex justify-content-end gap-2 mt-4">
        <CButton color="secondary" variant="outline" type="button" onClick={resetAll}>
          Cancel
        </CButton>
        <CLoadingButton type="submit" color="primary" loading={loader}>
          Apply Image to {matchedPlayers.length} Players
        </CLoadingButton>
      </div>
    </CForm>
  )
}

export default PlayerPositionBulkUpload
